import React, { useState } from "react";
import { FaPencilAlt, FaTrashAlt } from "react-icons/fa";
import { api } from "../api/api";
// L'esecutore dei gesti della plancia clinic: «Saving…», bottoni spenti
// durante la chiamata, e il messaggio del server nel riquadro d'errore.
import { useGestoClinic } from "./clinicShared";
// La veste della finestra «Payments» della PWA, importata e NON modificata:
// guscio §8 e campi (AdminTools.css), card, totali e tabella (PaymentsModal.css).
// ⚠️ UserPaymentsModal.css NON si importa: questa finestra non usa nessuna
// delle sue classi (v. il cappello di UserPaymentsModal.jsx).
import "../components/AdminTools.css";
import "../components/PaymentsModal.css";
import "./ClinicEvents.css";

/**
 * Clinic / Off Season / Specials — la finestra «Event payment» di UN iscritto
 * a UN evento. Cantiere CLINICS / OFFSEASON, Fetta 7 «pagamenti dell'evento»
 * (23/9/2026). La apre il bottone «Payments» della riga del partecipante,
 * nella pagina dell'evento (ClinicEventDetail.jsx).
 *
 * ⚠️ È UNA COPIA SEMPLIFICATA di components/UserPaymentsModal.jsx, e resta A SÉ
 * STANTE: file proprio, rotte proprie (/clinic-events/:id/participants/:regId
 * /fees*), dati propri (il campo `eventFees` dell'iscrizione). Della PWA non
 * importa codice né dati: il mondo clinic non tocca stagioni, `fees` e
 * `feesSeasons`.
 *
 * Tolto dalla copia: tutto ciò che è anno/stagione (la prop `season`, gli
 * estremi dell'annata, l'anno nel titolo e nei corpi), `quoteDellAnnata` e
 * utils/quote.js (la somma dei pagamenti è ricalcata qui sotto), la ricarica
 * da GET /users/:id, il ramo del «pagamento senza id» (qui ogni pagamento
 * nasce col suo, coniato dal server), gli alert. Al posto della «Special
 * Discount» c'è «Not due», che ha un motivo obbligatorio e cambia ciò che la
 * pagina scrive nella colonna Balance.
 *
 * Ogni rotta risponde con l'iscrizione intera (`{ ok, participant }`): la
 * finestra riprende da lì i pagamenti, e la pagina sostituisce la riga.
 *
 * Testi a schermo in inglese (CLAUDE.md §3.7).
 */

// I due motivi di «Not due», gli stessi del server.
const MOTIVI_NON_DOVUTO = [
  { valore: "included", etichetta: "Included" },
  { valore: "free", etichetta: "Free" },
];

// La quota dell'evento di un iscritto, coi ripieghi della nascita: una riga
// che non la porta (backend precedente, iscrizione mai toccata) vale quota 0,
// nessuna spunta, nessun pagamento — «$0.00», come la PWA.
function quoteEvento(iscritto) {
  const q =
    iscritto && iscritto.eventFees && typeof iscritto.eventFees === "object"
      ? iscritto.eventFees
      : {};
  const dovuto = Number(q.totalDue);

  return {
    totalDue: Number.isFinite(dovuto) ? dovuto : 0,
    notDue: q.notDue === true,
    notDueReason: typeof q.notDueReason === "string" ? q.notDueReason : "",
    payments: Array.isArray(q.payments) ? q.payments : [],
  };
}

// La somma dei pagamenti: calco di `totaleVersato` (utils/quote.js), ricopiato
// e non importato — il mondo clinic non dipende dalle quote del club.
function totalePagato(payments) {
  return payments.reduce((somma, p) => somma + Number(p && p.amount), 0);
}

// Il denaro a schermo, come la PWA: "$" + due decimali, arrotondamento SOLO
// qui, segno meno nativo ($-10.00).
// ⚠️ UNA SOLA DIFFERENZA DALLA PWA: lo zero con il segno. Le somme di decimali
// lasciano residui (0.3 − (0.1 + 0.2) = −0.00000000000000006) che `toFixed`
// scrive «-0.00»; qui si scrive «0.00». Il valore non si tocca.
export function importoAschermo(n) {
  const testo = Number(n).toFixed(2);
  return `$${testo === "-0.00" ? "0.00" : testo}`;
}

// IL SALDO DI UNA RIGA, per la colonna Balance della pagina dell'evento: una
// regola sola, qui accanto alla finestra che la mostra.
// Con «Not due» il saldo non si scrive — qualunque siano quota e pagamenti —
// e si scrive «Not due»; altrimenti quota − pagamenti, rosso se resta da
// pagare, verde se saldato (zero e negativo compresi, come la PWA).
export function saldoDellIscritto(iscritto) {
  const q = quoteEvento(iscritto);

  if (q.notDue) return { testo: "Not due", tono: "notdue" };

  const saldo = q.totalDue - totalePagato(q.payments);

  return { testo: importoAschermo(saldo), tono: Math.round(saldo * 100) > 0 ? "due" : "settled" };
}

const formatDate = (isoDate) => {
  if (!isoDate) return "";
  const [yyyy, mm, dd] = isoDate.split("-");
  return `${dd}-${mm}-${yyyy}`;
};

// Le descrizioni dell'icona «i» (doc 07 §11.3): cosa è la sezione e a cosa
// serve, riferite all'EVENTO (niente «club», niente «member»).
const DESCRIZIONI = {
  fees: "What this participant owes for this event.",
  payments: "Every payment recorded for this event.",
  newPayment: "Records a payment for this event.",
};

// La card con titolo oro e icona «i», copiata da UserPaymentsModal.jsx.
const Sezione = ({ chiave, titolo, aperta, onApri, children }) => (
  <section className="qpsc-pay-card">
    <div className="qpsc-pay-card__head">
      <h3 className="qpsc-pay-card__title">{titolo}</h3>

      <button
        type="button"
        className={"qpsc-vp-info" + (aperta ? " qpsc-vp-info--open" : "")}
        aria-expanded={aperta}
        aria-controls={`qpsc-clinic-pay-purpose-${chiave}`}
        aria-label={`What is “${titolo}”?`}
        onClick={() => onApri(chiave)}
      >
        <span className="qpsc-vp-info__glyph" aria-hidden="true">i</span>
      </button>
    </div>

    {aperta && (
      <p className="qpsc-vp-purpose" id={`qpsc-clinic-pay-purpose-${chiave}`}>
        {DESCRIZIONI[chiave]}
      </p>
    )}

    {children}
  </section>
);

export default function ClinicPaymentModal({ idEvento, iscritto, onAggiorna, onClose }) {
  const iniziali = quoteEvento(iscritto);

  // I campi della card «Fees» sono una BOZZA: finché non si preme «Update»
  // non sono sul server. La quota resta testo finché si scrive (un campo
  // vuoto non diventa zero in silenzio: lo dice il riquadro d'errore).
  const [totalDue, setTotalDue] = useState(String(iniziali.totalDue));
  const [notDue, setNotDue] = useState(iniziali.notDue);
  const [notDueReason, setNotDueReason] = useState(iniziali.notDueReason);
  const [payments, setPayments] = useState(iniziali.payments);

  const [newDate, setNewDate] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [newNote, setNewNote] = useState("");
  // `null` = si aggiunge; un id = si modifica quel pagamento (mai la posizione).
  const [editPaymentId, setEditPaymentId] = useState(null);

  const [descrizioneAperta, setDescrizioneAperta] = useState(null);
  const apriDescrizione = (chiave) =>
    setDescrizioneAperta((attuale) => (attuale === chiave ? null : chiave));

  const { azioneInCorso, erroreAzione, setErroreAzione, esegui } = useGestoClinic();
  const occupato = azioneInCorso !== null;

  const base = `/clinic-events/${idEvento}/participants/${iscritto.id}/fees`;

  const dovutoNumerico = Number(totalDue);
  const totalPaid = totalePagato(payments);
  const balance = (Number.isFinite(dovutoNumerico) ? dovutoNumerico : 0) - totalPaid;

  // Dopo ogni scrittura: la finestra riprende i pagamenti dal server e la
  // pagina sostituisce la riga con l'iscrizione restituita.
  const riprendi = (risposta) => {
    const iscrittoNuovo = risposta && risposta.participant;
    if (!iscrittoNuovo) return null;
    setPayments(quoteEvento(iscrittoNuovo).payments);
    onAggiorna(iscrittoNuovo);
    return iscrittoNuovo;
  };

  const svuotaModulo = () => {
    setNewDate("");
    setNewAmount("");
    setNewNote("");
    setEditPaymentId(null);
  };

  const aggiornaQuota = () => {
    if (String(totalDue).trim() === "" || !Number.isFinite(dovutoNumerico)) {
      setErroreAzione("Enter the total fees due (0 if there is none).");
      return;
    }

    if (notDue && !notDueReason) {
      setErroreAzione("Choose why the fee is not due: Included or Free.");
      return;
    }

    esegui(
      "quota",
      () =>
        api.put(base, {
          totalDue: dovutoNumerico,
          notDue,
          notDueReason: notDue ? notDueReason : "",
        }),
      (risposta) => {
        const iscrittoNuovo = riprendi(risposta);
        if (!iscrittoNuovo) return;
        const q = quoteEvento(iscrittoNuovo);
        setTotalDue(String(q.totalDue));
        setNotDue(q.notDue);
        setNotDueReason(q.notDueReason);
      }
    );
  };

  const salvaPagamento = () => {
    if (!newDate || String(newAmount).trim() === "") {
      setErroreAzione("A payment needs a date and an amount.");
      return;
    }

    const corpo = { amount: Number(newAmount), date: newDate, note: newNote };

    esegui(
      "pagamento",
      () =>
        editPaymentId !== null
          ? api.put(`${base}/payments/${encodeURIComponent(editPaymentId)}`, corpo)
          : api.post(`${base}/payments`, corpo),
      (risposta) => {
        if (riprendi(risposta)) svuotaModulo();
      }
    );
  };

  const cancellaPagamento = (p) => {
    if (!window.confirm("Are you sure you want to delete this payment?")) return;

    esegui(
      `cancella-${p.id}`,
      () => api.del(`${base}/payments/${encodeURIComponent(p.id)}`),
      (risposta) => {
        // Se si cancella il pagamento in modifica, il modulo torna ad «Add»:
        // resterebbe appeso a un id che non esiste più (come nella PWA).
        if (editPaymentId === p.id) svuotaModulo();
        riprendi(risposta);
      }
    );
  };

  const modificaPagamento = (p) => {
    setErroreAzione(null);
    setNewDate(p.date || "");
    setNewAmount(String(p.amount));
    setNewNote(p.note || "");
    setEditPaymentId(p.id);
  };

  const nome = `${iscritto.childFirstName || ""} ${iscritto.childLastName || ""}`.trim();

  return (
    <div className="qpsc-modal-overlay">
      <div className="qpsc-modal qpsc-pay-modal" role="dialog" aria-modal="true">
        <h2 className="qpsc-modal__title">Event payment for {nome}</h2>

        <div className="qpsc-modal__body">
          {erroreAzione && (
            <p className="qpsc-clinic__error" role="alert">
              {erroreAzione}
            </p>
          )}

          <Sezione
            chiave="fees"
            titolo="Fees"
            aperta={descrizioneAperta === "fees"}
            onApri={apriDescrizione}
          >
            <div className="qpsc-pay-fields">
              <div className="qpsc-field">
                <label className="qpsc-field__label" htmlFor="qpsc-clinic-pay-total-due">
                  Total Fees Due
                </label>
                <input
                  id="qpsc-clinic-pay-total-due"
                  className="qpsc-input qpsc-pay-input"
                  type="number"
                  value={totalDue}
                  onChange={(e) => setTotalDue(e.target.value)}
                />
              </div>

              <label className="qpsc-check">
                <input
                  className="qpsc-check__input"
                  type="checkbox"
                  checked={notDue}
                  onChange={(e) => setNotDue(e.target.checked)}
                />
                <span className="qpsc-check__box" aria-hidden="true" />
                <span>Not due</span>
              </label>

              {/* Il motivo ESISTE SOLO con la spunta, ed è obbligatorio. */}
              {notDue && (
                <div className="qpsc-field">
                  <label className="qpsc-field__label" htmlFor="qpsc-clinic-pay-reason">
                    Reason
                  </label>
                  <select
                    id="qpsc-clinic-pay-reason"
                    className="qpsc-input qpsc-pay-input"
                    value={notDueReason}
                    onChange={(e) => setNotDueReason(e.target.value)}
                  >
                    <option value="">Choose a reason</option>
                    {MOTIVI_NON_DOVUTO.map((m) => (
                      <option key={m.valore} value={m.valore}>
                        {m.etichetta}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="qpsc-pay-totals">
                <p className="qpsc-pay-total">
                  <span className="qpsc-pay-total__label">Total Paid</span>
                  <span className="qpsc-pay-total__value">{importoAschermo(totalPaid)}</span>
                </p>
                <p className="qpsc-pay-total">
                  <span className="qpsc-pay-total__label">Balance</span>
                  {notDue ? (
                    <span className="qpsc-pay-total__value">Not due</span>
                  ) : (
                    <span
                      className={
                        "qpsc-pay-total__value " +
                        (Math.round(balance * 100) > 0
                          ? "qpsc-pay-total__value--due"
                          : "qpsc-pay-total__value--settled")
                      }
                    >
                      {importoAschermo(balance)}
                    </span>
                  )}
                </p>
              </div>

              <div className="qpsc-pay-actions">
                <button
                  type="button"
                  className="qpsc-tool-btn"
                  onClick={aggiornaQuota}
                  disabled={occupato}
                >
                  {azioneInCorso === "quota" ? "Saving…" : "Update"}
                </button>
              </div>
            </div>
          </Sezione>

          <Sezione
            chiave="payments"
            titolo="Payments"
            aperta={descrizioneAperta === "payments"}
            onApri={apriDescrizione}
          >
            <table className="qpsc-pay-table">
              <thead className="qpsc-pay-table__head">
                <tr>
                  <th className="qpsc-pay-table__col" scope="col">Date</th>
                  <th className="qpsc-pay-table__col" scope="col">Amount</th>
                  <th className="qpsc-pay-table__col" scope="col">Note</th>
                  <th className="qpsc-pay-table__col" scope="col">Actions</th>
                </tr>
              </thead>
              <tbody className="qpsc-pay-table__body">
                {payments.map((p, index) => (
                  <tr key={p.id || `senza-id-${index}`} className="qpsc-pay-table__row">
                    <td className="qpsc-pay-table__cell" data-label="Date">
                      {formatDate(p.date)}
                    </td>
                    <td className="qpsc-pay-table__cell" data-label="Amount">
                      {importoAschermo(p.amount)}
                    </td>
                    <td className="qpsc-pay-table__cell" data-label="Note">
                      {p.note}
                    </td>
                    <td
                      className="qpsc-pay-table__cell qpsc-pay-table__cell--actions"
                      data-label="Actions"
                    >
                      <span className="qpsc-pay-tools">
                        <button
                          type="button"
                          className="qpsc-ghost-btn qpsc-pay-icon-btn"
                          aria-label="Edit this payment"
                          onClick={() => modificaPagamento(p)}
                          disabled={occupato}
                        >
                          <FaPencilAlt aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="qpsc-ghost-btn qpsc-pay-icon-btn"
                          aria-label="Delete this payment"
                          onClick={() => cancellaPagamento(p)}
                          disabled={occupato}
                        >
                          <FaTrashAlt aria-hidden="true" />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Sezione>

          <Sezione
            chiave="newPayment"
            titolo={editPaymentId !== null ? "Edit Payment" : "Add New Payment"}
            aperta={descrizioneAperta === "newPayment"}
            onApri={apriDescrizione}
          >
            <div className="qpsc-pay-fields">
              <div className="qpsc-field">
                <label className="qpsc-field__label" htmlFor="qpsc-clinic-pay-date">
                  Date
                </label>
                <input
                  id="qpsc-clinic-pay-date"
                  className="qpsc-input qpsc-pay-input"
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                />
              </div>

              <div className="qpsc-field">
                <label className="qpsc-field__label" htmlFor="qpsc-clinic-pay-amount">
                  Amount
                </label>
                <input
                  id="qpsc-clinic-pay-amount"
                  className="qpsc-input qpsc-pay-input"
                  type="number"
                  placeholder="Amount"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                />
              </div>

              <div className="qpsc-field">
                <label className="qpsc-field__label" htmlFor="qpsc-clinic-pay-note">
                  Note
                </label>
                <input
                  id="qpsc-clinic-pay-note"
                  className="qpsc-input qpsc-pay-input"
                  type="text"
                  placeholder="Note"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                />
              </div>

              <div className="qpsc-pay-actions">
                <button
                  type="button"
                  className="qpsc-tool-btn"
                  onClick={salvaPagamento}
                  disabled={occupato}
                >
                  {azioneInCorso === "pagamento"
                    ? "Saving…"
                    : editPaymentId !== null
                    ? "Save"
                    : "Add"}
                </button>
              </div>
            </div>
          </Sezione>
        </div>

        <div className="qpsc-modal__actions">
          <button type="button" className="qpsc-ghost-btn" onClick={onClose} disabled={occupato}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
